// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { CapabilityRuntimeState } from '@/generated/physicsAgentState';

export type MessageCapabilityRuntime = CapabilityRuntimeState & {
  performance?: Record<string, unknown>;
};

type RuntimeField = keyof MessageCapabilityRuntime;

const RUNTIME_FIELDS: RuntimeField[] = [
  'active_skills',
  'selected_skill_ids',
  'skill_policy',
  'forked_skill_runs',
  'fork_trace_summaries',
  'performance',
];

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : undefined;
}

function recordArray(value: unknown): Array<Record<string, unknown>> | undefined {
  return Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> =>
          Boolean(item) && typeof item === 'object' && !Array.isArray(item)
      )
    : undefined;
}

function recordOrNull(value: unknown): Record<string, unknown> | null | undefined {
  if (value === null) return null;
  return asRecord(value) ?? undefined;
}

function readRuntimeField(source: Record<string, unknown>, field: RuntimeField): unknown {
  const value = source[field];
  if (field === 'active_skills' || field === 'selected_skill_ids') return stringArray(value);
  if (field === 'forked_skill_runs' || field === 'fork_trace_summaries') return recordArray(value);
  if (field === 'skill_policy') return recordOrNull(value);
  if (field === 'performance') return asRecord(value) ?? undefined;
  return undefined;
}

function hasRuntimeValue(value: unknown): boolean {
  return value !== undefined;
}

export function normalizeCapabilityRuntimeSources(
  ...sources: unknown[]
): MessageCapabilityRuntime | undefined {
  const records = sources.map(asRecord).filter((source): source is Record<string, unknown> => Boolean(source));
  const runtime: MessageCapabilityRuntime = {};

  for (const field of RUNTIME_FIELDS) {
    for (const source of records) {
      const value = readRuntimeField(source, field);
      if (!hasRuntimeValue(value)) continue;
      (runtime as Record<string, unknown>)[field] = value;
      break;
    }
  }

  return Object.keys(runtime).length > 0 ? runtime : undefined;
}

export function mergeCapabilityRuntime(
  current: MessageCapabilityRuntime | undefined,
  next: MessageCapabilityRuntime | undefined
): MessageCapabilityRuntime | undefined {
  if (!current) return next;
  if (!next) return current;
  return {
    ...current,
    ...Object.fromEntries(
      Object.entries(next).filter(([, value]) => value !== undefined)
    ),
  };
}

export function getActiveSkills(runtime: MessageCapabilityRuntime | undefined): string[] {
  return Array.isArray(runtime?.active_skills) ? runtime.active_skills : [];
}

export function getSelectedSkillIds(runtime: MessageCapabilityRuntime | undefined): string[] {
  return Array.isArray(runtime?.selected_skill_ids) ? runtime.selected_skill_ids : [];
}

export function getSkillPolicy(
  runtime: MessageCapabilityRuntime | undefined
): Record<string, unknown> | null | undefined {
  return runtime?.skill_policy && typeof runtime.skill_policy === 'object'
    ? (runtime.skill_policy as Record<string, unknown>)
    : runtime?.skill_policy;
}

export function getForkTraceItems(
  runtime: MessageCapabilityRuntime | undefined
): Array<Record<string, unknown>> {
  if (Array.isArray(runtime?.fork_trace_summaries) && runtime.fork_trace_summaries.length > 0) {
    return runtime.fork_trace_summaries;
  }
  return Array.isArray(runtime?.forked_skill_runs) ? runtime.forked_skill_runs : [];
}
